import React from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";

import { buildMoneySummary, countOverdueOpenInvoices, type MoneySummary, type OverdueOpenInvoiceCount } from "../../lib/moneyTruth";
import { fetchMoneyViewData } from "../../services/invoiceService";

/**
 * PKT-0916 (C) · Money, derived from invoices + clients (no new table).
 *
 *   Owed        sent + overdue invoices: count, $ total, oldest days late
 *   Collected   paid invoices by paid_at: this month · last 90 days
 *   Awaiting    moved in, Applied / Payment Received, no live invoice — the
 *               same rule as the "Send invoice" nudge on Needs a touch
 *
 * READ-ONLY and self-fetching, like TenPctPathLine. Figures cover the
 * invoices the viewer can read (RLS: own/assigned, or admin/manager). A failed
 * read renders "unavailable" — never a fabricated $0.
 */

const usd = (n: number) =>
  `$${
    Number.isInteger(n)
      ? n.toLocaleString("en-US")
      : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const Row: React.FC<{ testId: string; label: string; main: string; detail?: string }> = ({
  testId,
  label,
  main,
  detail,
}) => (
  <Box data-testid={testId}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
      {main}
    </Typography>
    {detail && (
      <Typography variant="caption" color="text.secondary" display="block">
        {detail}
      </Typography>
    )}
  </Box>
);

const MoneyPanel: React.FC<{ now?: Date }> = ({ now }) => {
  const [summary, setSummary] = React.useState<(MoneySummary & { overdueOpen: OverdueOpenInvoiceCount }) | null>(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setSummary(null);
    setFailed(false);
    fetchMoneyViewData()
      .then(({ invoices, clients }) => {
        if (!cancelled) {
          const evaluatedAt = now ?? new Date();
          setSummary({
            ...buildMoneySummary(invoices, clients, evaluatedAt),
            overdueOpen: countOverdueOpenInvoices(invoices, clients, evaluatedAt),
          });
        }
      })
      .catch((err) => {
        console.error("money panel: could not read invoices/clients", err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [now]);

  return (
    <Card sx={{ mb: 3 }} data-testid="money-panel">
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
          Money
        </Typography>
        {failed ? (
          <Typography variant="body2" color="error" data-testid="money-unavailable">
            Money figures are unavailable right now (could not read invoices).
          </Typography>
        ) : !summary ? (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Box data-testid="money-overdue-open">
              <Row
                testId="money-overdue-open-count"
                label="Overdue open invoice records"
                main={String(summary.overdueOpen.overdueCount)}
                detail={`As of ${summary.overdueOpen.asOfDate} (local calendar). Current sent/overdue records due before this date; due today excluded.`}
              />
              <Typography variant="caption" display="block">
                Scope: {summary.overdueOpen.loadedCount} loaded viewer-readable invoices; {summary.overdueOpen.openCount} open records after known test/synthetic exclusions.
                This is not a business-wide total or the Clients owner filter. Current statuses, not historical balances. Count only; not revenue, leases or collected payments.
              </Typography>
              <Typography variant="caption" display="block">
                Needs review (excluded from overdue count): {summary.overdueOpen.dueDateReviewCount} missing/invalid due dates; {summary.overdueOpen.paymentReviewCount} open records with paid-date evidence (payment review takes precedence).
              </Typography>
              <Typography variant="caption" display="block">
                Coverage: {summary.overdueOpen.excludedTestCount} known test/synthetic open records excluded; {summary.overdueOpen.clientCoverageUnknownCount} open records with unverified client test flags.
                Partial payments are not represented in this read model. Source: loaded invoice status, due_date and paid_at.
              </Typography>
            </Box>
            <Row
              testId="money-owed"
              label="Owed"
              main={usd(summary.owed.total)}
              detail={`${plural(summary.owed.count, "invoice", "invoices")} sent or overdue · ${
                summary.owed.oldestDaysLate === null ? "none late" : `oldest ${summary.owed.oldestDaysLate}d late`
              }`}
            />
            <Row
              testId="money-collected"
              label="Collected"
              main={`${usd(summary.collectedThisMonth.total)} this month`}
              detail={`${usd(summary.collectedLast90.total)} last 90 days · ${plural(
                summary.collectedLast90.count,
                "invoice",
                "invoices"
              )} (by the date marked paid)`}
            />
            <Row
              testId="money-awaiting"
              label="Awaiting invoice"
              main={String(summary.awaitingInvoice)}
              detail="moved in, no invoice sent — see Clients › Needs a touch"
            />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default MoneyPanel;
